import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Stack,
  Container,
  Paper,
  Grid,
  IconButton
} from '@mui/material';
import {
  Download,
  Image as ImageIcon,
  Label as LabelIcon,
  Info as InfoIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';

const ControlNetPoseEditor = ({onSelectImage}) => {
  const initialJoints = {
    nose: { x: 200, y: 40 },
    neck: { x: 200, y: 80 },
    rightShoulder: { x: 230, y: 100 },
    rightElbow: { x: 260, y: 130 },
    rightWrist: { x: 280, y: 160 },
    leftShoulder: { x: 170, y: 100 },
    leftElbow: { x: 140, y: 130 },
    leftWrist: { x: 120, y: 160 },
    rightHip: { x: 220, y: 180 },
    rightKnee: { x: 230, y: 250 },
    rightAnkle: { x: 230, y: 320 },
    leftHip: { x: 180, y: 180 },
    leftKnee: { x: 170, y: 250 },
    leftAnkle: { x: 170, y: 320 },
    rightEye: { x: 210, y: 30 },
    leftEye: { x: 190, y: 30 },
    rightEar: { x: 220, y: 35 },
    leftEar: { x: 180, y: 35 },
  };

  const adjustInitialJoints = (canvasWidth, canvasHeight) => {
    const adjustedJoints = {};
    for (const jointName in initialJoints) {
      adjustedJoints[jointName] = {
        x: initialJoints[jointName].x * (canvasWidth / 400),
        y: initialJoints[jointName].y * (canvasHeight / 400),
      };
    }
    return adjustedJoints;
  };


  const [joints, setJoints] = useState(() => adjustInitialJoints(400, 400));
  const [draggedJoint, setDraggedJoint] = useState(null);
  const [showKeypoints, setShowKeypoints] = useState(false);
  const [resolution, setResolution] = useState('512x512');
  const [customWidth, setCustomWidth] = useState(512);
  const [customHeight, setCustomHeight] = useState(512);
  const svgRef = useRef(null);
  const [lineWidth, setLineWidth] = useState(3);
  const [jointRadius, setJointRadius] = useState(8);
  const [svgWidth, setSvgWidth] = useState(400);
  const [svgHeight, setSvgHeight] = useState(400);
  const [isDraggingCanvas, setIsDraggingCanvas] = useState(false);
  const [dragStartPoint, setDragStartPoint] = useState({ x: 0, y: 0 });
  const [poseScale, setPoseScale] = useState(1.0);

  const commonResolutions = [
    { value: '512x512', label: '512×512' },
    { value: '768x768', label: '768×768' },
    { value: '1024x1024', label: '1024×1024' },
    { value: '640x960', label: '640×960' },
    { value: '960x640', label: '960×640' },
    { value: 'custom', label: 'カスタム' },
  ];

  const jointColors = {
    'face': '#FF0000',
    'torso': '#00FF00',
    'rightArm': '#0000FF',
    'leftArm': '#FFFF00',
    'rightLeg': '#FF00FF',
    'leftLeg': '#00FFFF'
  };

  const jointGroups = {
    nose: 'face',
    neck: 'torso',
    rightShoulder: 'rightArm',
    rightElbow: 'rightArm',
    rightWrist: 'rightArm',
    leftShoulder: 'leftArm',
    leftElbow: 'leftArm',
    leftWrist: 'leftArm',
    rightHip: 'rightLeg',
    rightKnee: 'rightKnee',
    rightAnkle: 'rightAnkle',
    leftHip: 'leftLeg',
    leftKnee: 'leftKnee',
    leftAnkle: 'leftAnkle',
    rightEye: 'face',
    leftEye: 'face',
    rightEar: 'face',
    leftEar: 'face',
  };

  useEffect(() => {
    if (resolution === 'custom') {
      setSvgWidth(customWidth);
      setSvgHeight(customHeight);
      setJoints(adjustInitialJoints(customWidth, customHeight));
    } else {
      const [width, height] = resolution.split('x').map(Number);
      setSvgWidth(width);
      setSvgHeight(height);
      setJoints(adjustInitialJoints(width, height));
    }
  }, [resolution, customWidth, customHeight]);

  const getDistance = (p1, p2) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  const getMidpoint = (p1, p2) => {
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2
    };
  };

  const getAngle = (p1, p2) => {
    return Math.atan2(p2.y - p1.y, p2.x - p1.x);
  };

  const handleCanvasMouseDown = (e) => {
    setIsDraggingCanvas(true);
    setDragStartPoint({ x: e.clientX, y: e.clientY });
  };

  const handleCanvasMouseMove = (e) => {
    if (!isDraggingCanvas) return;

    const svgRect = svgRef.current.getBoundingClientRect();
    const deltaX = e.clientX - dragStartPoint.x;
    const deltaY = e.clientY - dragStartPoint.y;

    setJoints(prevJoints => {
      const updatedJoints = {};
      for (const jointName in prevJoints) {
        updatedJoints[jointName] = {
          x: prevJoints[jointName].x + deltaX / poseScale,
          y: prevJoints[jointName].y + deltaY / poseScale
        };
      }
      return updatedJoints;
    });

    setDragStartPoint({ x: e.clientX, y: e.clientY });
  };

  const handleCanvasMouseUp = () => {
    setIsDraggingCanvas(false);
  };


  const handleMouseDown = (jointName, e) => {
    e.stopPropagation();
    setDraggedJoint(jointName);
  };

  const handleMouseMove = (e) => {
    if (!draggedJoint) return;

    const svgRect = svgRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(svgWidth, (e.clientX - svgRect.left) / poseScale));
    const y = Math.max(0, Math.min(svgHeight, (e.clientY - svgRect.top) / poseScale));

    setJoints(prev => ({
      ...prev,
      [draggedJoint]: { x, y }
    }));
  };

  const handleMouseUp = () => {
    setDraggedJoint(null);
  };

  const exportPose = () => {
    const poseData = exportPoseData();

    const blob = new Blob([JSON.stringify(poseData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pose_keypoints.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportImage = () => {
    const [width, height] = resolution === 'custom'
      ? [customWidth, customHeight]
      : resolution.split('x').map(Number);

    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const svg = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svg);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pose_${width}x${height}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    };
    img.src = url;
  };

  const getImage = () => {
    // 解像度の取得
    const [width, height] = resolution === 'custom'
      ? [customWidth, customHeight]
      : resolution.split('x').map(Number);
  
    // SVGデータの取得と変換
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const svg = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svg);
  
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        // Canvas の作成
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
  
        // 背景色を設定
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // SVG を Canvas に描画
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  
        // Canvas から DataURL を取得
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      };
  
      img.onerror = reject;
      img.src = url;
    })
      .finally(() => {
        // 使用済みの URL を解放
        URL.revokeObjectURL(url);
      });
  };

  const mixColor = (color1, color2) => {
    const c1 = color1.match(/#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})/);
    const c2 = color2.match(/#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})/);
    if (!c1 || !c2) return '#FFFFFF';

    const r = Math.round((parseInt(c1[1], 16) + parseInt(c2[1], 16)) / 2).toString(16).padStart(2, '0');
    const g = Math.round((parseInt(c1[2], 16) + parseInt(c2[2], 16)) / 2).toString(16).padStart(2, '0');
    const b = Math.round((parseInt(c1[3], 16) + parseInt(c2[3], 16)) / 2).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  };


  const drawLine = (start, end) => {
    const startJoint = joints[start];
    const endJoint = joints[end];
    if (!startJoint || !endJoint) return null;

    const startType = jointGroups[start] || 'torso';
    const endType = jointGroups[end] || 'torso';
    const startColor = jointColors[startType] || '#FFFFFF';
    const endColor = jointColors[endType] || '#FFFFFF';
    const lineColor = mixColor(startColor, endColor);

    const distance = getDistance(startJoint, endJoint);
    const midpoint = getMidpoint(startJoint, endJoint);
    const angle = getAngle(startJoint, endJoint) * 180 / Math.PI;

    return (
      <ellipse
        key={`${start}-${end}`}
        cx={midpoint.x * poseScale}
        cy={midpoint.y * poseScale}
        rx={distance / 2 * poseScale}
        ry={lineWidth * 1.5 * poseScale}
        fill={lineColor}
        transform={`rotate(${angle} ${midpoint.x * poseScale} ${midpoint.y * poseScale})`}
        opacity="0.7"
      />
    );
  };

  const drawJoint = (name, { x, y }) => {
    const jointType = jointGroups[name] || 'torso';
    const color = jointColors[jointType] || '#FFFFFF';

    return (
      <g key={name}>
        <circle
          cx={x * poseScale}
          cy={y * poseScale}
          r={jointRadius * poseScale}
          fill={draggedJoint === name ? '#ff0000' : color}
          opacity="0.7"
          cursor="pointer"
          onMouseDown={(e) => handleMouseDown(name, e)}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
        {showKeypoints && (
          <text
            x={(x + 10) * poseScale}
            y={(y - 10) * poseScale}
            fontSize={12 * poseScale}
            fill="#666"
          >
            {name}
          </text>
        )}
      </g>
    );
  };

  const getCanvasScale = () => {
    return poseScale;
  }


  const connections = [
    ['nose', 'neck'],
    ['neck', 'rightShoulder'],
    ['rightShoulder', 'rightElbow'],
    ['rightElbow', 'rightWrist'],
    ['neck', 'leftShoulder'],
    ['leftShoulder', 'leftElbow'],
    ['leftElbow', 'leftWrist'],
    ['neck', 'rightHip'],
    ['rightHip', 'rightKnee'],
    ['rightKnee', 'rightAnkle'],
    ['neck', 'leftHip'],
    ['leftHip', 'leftKnee'],
    ['leftKnee', 'leftAnkle'],
    ['nose', 'rightEye'],
    ['rightEye', 'rightEar'],
    ['nose', 'leftEye'],
    ['leftEye', 'leftEar'],
  ];

  const handleScaleUp = () => {
    setPoseScale(prevScale => Math.min(prevScale + 0.1, 5.0));
  };

  const handleScaleDown = () => {
    setPoseScale(prevScale => Math.max(prevScale - 0.1, 0.1));
  };

  const handleImportJson = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const poseData = JSON.parse(e.target.result);
        if (poseData && poseData.people && poseData.people[0] && poseData.people[0].pose_keypoints_2d) {
          const keypoints2d = poseData.people[0].pose_keypoints_2d;
          const importedJoints = {};
          let jointIndex = 0;
          Object.keys(initialJoints).forEach(jointName => {
            importedJoints[jointName] = {
              x: keypoints2d[jointIndex * 3],
              y: keypoints2d[jointIndex * 3 + 1]
            };
            jointIndex++;
          });
          setJoints(importedJoints);
          alert('JSONファイルのインポートに成功しました');
        } else {
          alert('JSONファイルの形式が正しくありません');
        }
      } catch (error) {
        alert('JSONファイルのパースに失敗しました');
        console.error('JSONパースエラー:', error);
      }
    };
    reader.readAsText(file);
    event.target.value = null;
  };

  const exportPoseData = () => {
    const keypointsList = Object.entries(joints).map(([name, pos]) => {
      return [pos.x, pos.y, 1];
    });

    return {
      version: "1.0",
      people: [{
        pose_keypoints_2d: keypointsList.flat()
      }]
    };
  };


  const handleSaveToServer = async () => {
    const poseData = exportPoseData();

    try {
      const response = await fetch('save_pose.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(poseData),
      });

      if (response.ok) {
        alert('ポーズをサーバーに保存しました');
      } else {
        alert('サーバーへの保存に失敗しました');
        console.error('サーバーエラー:', response.status, response.statusText);
      }
    } catch (error) {
      alert('サーバーへのリクエストに失敗しました');
      console.error('Fetchエラー:', error);
    }
  };


  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card>
        <CardHeader
          title={
            <Typography variant="h5">
              ポーズ編集
            </Typography>
          }
        />
        {/* CardContent の vertical padding を調整 (pt: 2, pb: 1) */}
        <CardContent sx={{ pt: 0, pb: 0 }}>
          <Stack spacing={2}> {/* Stack コンポーネントの vertical spacing を調整 (spacing={2}) */}


            <Paper
              sx={{
                bgcolor: 'black',
                p: 1,
                width: 'fit-content'
              }}
            >
              <svg
                ref={svgRef}
                width={svgWidth}
                height={svgHeight}
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseDown={handleCanvasMouseDown}
              >
                {connections.map(([start, end]) => drawLine(start, end))}
                {Object.entries(joints).map(([name, pos]) => drawJoint(name, pos))}
              </svg>
            </Paper>

            {/* 解像度設定 Grid コンテナのマージンを調整 (mb: 1) */}
            <Grid container spacing={2} alignItems="center" mb={1}>
              <Grid item>
                <Button variant='contained' color='secondary' onClick={() => {
                  getImage().then(png => {
                    onSelectImage(png)
                  })
                }}>決定</Button>
              </Grid>
              <Grid item>
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>解像度</InputLabel>
                  <Select
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    label="解像度"
                  >
                    {commonResolutions.map(res => (
                      <MenuItem key={res.value} value={res.value}>
                        {res.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {resolution === 'custom' && (
                <Grid item>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      label="幅"
                      type="number"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(Number(e.target.value))}
                      inputProps={{ min: 64, max: 2048 }}
                      sx={{ width: 100 }}
                    />
                    <Typography>×</Typography>
                    <TextField
                      label="高さ"
                      type="number"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(Number(e.target.value))}
                      inputProps={{ min: 64, max: 2048 }}
                      sx={{ width: 100 }}
                    />
                  </Stack>
                </Grid>
              )}
            </Grid>

            {/* 操作ボタン Grid コンテナのマージンを調整 (mb: 1) */}
            <Grid container spacing={2} alignItems="center" mb={1}>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<ImageIcon />}
                  onClick={exportImage}
                >
                  画像出力
                </Button>
              </Grid>
              <Grid item className='d-none'>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={exportPose}
                >
                  DL
                </Button>
              </Grid>
              <Grid item className='d-none'>
                <Button
                  variant="outlined"
                  startIcon={<FileUploadIcon />}
                  onClick={() => document.getElementById('json-import-file').click()}
                >
                  UP
                </Button>
                <input
                  type="file"
                  id="json-import-file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={handleImportJson}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<LabelIcon />}
                  onClick={() => setShowKeypoints(!showKeypoints)}
                >
                  {showKeypoints ? 'ラベル非表示' : 'ラベル表示'}
                </Button>
              </Grid>
              <Grid item className='d-none'>
                <Button
                  variant="contained"
                  onClick={handleSaveToServer}
                >
                  サーバー保存
                </Button>
              </Grid>
            </Grid>

            {/* 設定項目 Grid コンテナのマージンを調整 (mb: 1) */}
            <Grid container spacing={2} alignItems="center" mb={1}>
              <Grid item>
                <FormControl sx={{ minWidth: 120 }}>
                  <TextField
                    label="線の太さ"
                    type="number"
                    value={lineWidth}
                    onChange={(e) => setLineWidth(Number(e.target.value))}
                    inputProps={{ min: 1, max: 20 }}
                    sx={{ width: 100 }}
                  />
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl sx={{ minWidth: 120 }}>
                  <TextField
                    label="関節の半径"
                    type="number"
                    value={jointRadius}
                    onChange={(e) => setJointRadius(Number(e.target.value))}
                    inputProps={{ min: 4, max: 20 }}
                    sx={{ width: 100 }}
                  />
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl sx={{ minWidth: 120 }}>
                  <TextField
                    label="スケール"
                    type="number"
                    value={poseScale}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ width: 100 }}
                  />
                </FormControl>
                <Button variant="outlined" onClick={handleScaleDown} sx={{ minWidth: 40 }}>-</Button>
                <Button variant="outlined" onClick={handleScaleUp} sx={{ minWidth: 40 }}>+</Button>
              </Grid>
            </Grid>

            <Stack spacing={1}>
              {[
                '点をドラッグしてポーズを調整',
                'キャンバスをドラッグして人形の位置を移動',
              ].map((text, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ControlNetPoseEditor;